/**
 * Calendrier de saisie des notes.
 *
 * La modale « + Ajouter une note » convient à une note : on ouvre, on saisit, on ferme. Elle
 * devient pénible pour dix, et surtout elle ne montre rien de ce qui est déjà documenté sur
 * la période. Cet écran répond aux deux : les notes existantes s'y voient dans le mois, et on
 * en ajoute en désignant un jour ou une plage à la souris, sans jamais le quitter.
 *
 * **La portée vit dans l'en-tête**, pas dans la case. Une case de calendrier n'a la place ni
 * d'un formulaire ni de quatre sélecteurs ; et redire le client à chaque note irait contre le
 * but de l'écran, qui est d'en saisir plusieurs d'affilée.
 *
 * **Le sujet fait exception** : l'en-tête n'en donne que la valeur d'office, et la barre de
 * saisie la reprend en la laissant corriger pour cette note-là. C'est le seul réglage qui
 * change d'une note à l'autre dans une même série — le bug relevé au milieu d'une opération
 * commerciale — et le seul qui se voie ensuite dans la grille, par la couleur.
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Calendar as IconeCalendrier, ChevronLeft, ChevronRight, Loader, Target, X } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import NotesModal from './NotesModal';
import { NOTE_TOPICS, couleurDeNote, sujetDeNote } from '../constants/noteTopics';
import { libellePlateforme } from '../constants/plateformes';
import PorteeDeNote, { STYLE_SELECTEUR } from './notes/PorteeDeNote';
import { usePorteeDeNote } from './notes/usePorteeDeNote';
import { feriesDeLaPeriode } from '../utils/joursFeries';
import { enFrancais } from './PeriodSelector';

const API_URL = config.API_URL;

const JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * Le calendrier ne montre pas un mois mais un ruban continu de semaines, qu'on fait défiler.
 *
 * Une grille mensuelle interdisait la seule chose qu'on lui demandait vraiment : tracer une
 * période à cheval sur deux mois. Le tracé se serait interrompu au changement de mois, faute
 * de jours voisins dans le DOM.
 *
 * Le ruban part de quelques mois autour d'aujourd'hui et s'allonge quand on approche d'un
 * bord, dans un sens comme dans l'autre.
 */
const MOIS_AVANT = 2;
const MOIS_APRES = 3;
/** Mois ajoutés à chaque allongement. */
const PAS = 3;
/** Garde-fou : au-delà, on cesse d'allonger plutôt que de laisser le DOM enfler sans fin. */
const PLAGE_MAX = 72;
/** Distance à un bord, en pixels, qui déclenche l'allongement. */
const SEUIL_BORD = 400;
/** Bande, en pixels, où le curseur fait défiler pendant un tracé. */
const BORD_ENTRAINEMENT = 56;
/** Pixels par battement d'entraînement. */
const VITESSE = 14;

/** Écart entre deux cases, en pixels. Sert aussi à étirer le champ de saisie sur la sélection. */
const ECART = 6;

/**
 * Date au format `YYYY-MM-DD`, sur le fuseau local.
 *
 * `toISOString()` conviendrait pour un instant, pas pour une borne de calendrier : à l'est de
 * Greenwich, minuit local tombe la veille en UTC, et le 1er du mois deviendrait le 31.
 */
const enIso = (d) => {
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jour = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jour}`;
};

const decale = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

/** Clé d'un mois, `AAAA-MM`. Sert d'identité aux ancres de défilement. */
const cleMois = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const NotesCalendar = ({ token, clients = [], sites = [], accounts = [], selectedClientId, selectedSiteId }) => {
  /**
   * Plage rendue : le premier mois, et combien de mois suivent. L'allongement ne fait que
   * reculer `premier` ou augmenter `nb` — les jours déjà rendus ne bougent jamais d'identité,
   * ce qui garde le tracé en cours valide pendant qu'on déborde.
   */
  const [plage, setPlage] = useState(() => {
    const a = new Date();
    return {
      premier: new Date(a.getFullYear(), a.getMonth() - MOIS_AVANT, 1),
      nb: MOIS_AVANT + 1 + MOIS_APRES
    };
  });

  /** Mois lu en tête de la zone visible : c'est lui qui s'écrit dans l'en-tête. */
  const [moisVisible, setMoisVisible] = useState(() => {
    const a = new Date();
    return new Date(a.getFullYear(), a.getMonth(), 1);
  });

  /**
   * Portée de la saisie, tenue par le hook partagé avec l'import.
   *
   * Décoché par défaut pour les graphiques, comme dans la modale : l'écran sert à saisir en
   * série, et cocher d'office annoterait tous les graphiques du dashboard pour une dizaine de
   * notes qu'on ne voulait qu'archiver.
   *
   * Une saisie en attente est abandonnée quand le header impose une autre portée : elle avait
   * été ouverte pour un client, elle serait enregistrée pour un autre.
   */
  const portee = usePorteeDeNote({
    sites,
    accounts,
    selectedClientId,
    selectedSiteId,
    auChangement: () => { setSaisie(null); setTexte(''); }
  });
  const { clientId, siteId, sujet, surGraphique } = portee;

  const [notes, setNotes] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  /**
   * Tracé en cours : l'ancre est le jour où l'on a appuyé, le curseur celui qu'on survole.
   *
   * Doublés en `ref` parce que le relâchement est écouté sur `window` : l'écouteur est posé
   * une fois pour toutes et ne verrait, sans cela, que les valeurs du premier rendu.
   */
  const [ancre, setAncre] = useState(null);
  const [curseur, setCurseur] = useState(null);
  const ancreRef = useRef(null);
  const curseurRef = useRef(null);
  const traceEnCours = useRef(false);

  const poserAncre = (iso) => { ancreRef.current = iso; setAncre(iso); };
  const poserCurseur = (iso) => { curseurRef.current = iso; setCurseur(iso); };

  // Sélection figée, en attente d'un contenu.
  const [saisie, setSaisie] = useState(null);
  const [texte, setTexte] = useState('');

  /**
   * Le sujet de la note en cours de saisie.
   *
   * Celui de l'en-tête reste la valeur d'office — c'est ce qui permet d'enchaîner dix notes
   * d'une opération commerciale sans y revenir — mais il n'est plus le seul mot : une note
   * qui sort du lot se corrige ici, au moment où on l'écrit, sans rouvrir la modale.
   *
   * Lu par `ref` : l'initialisation ne doit se produire qu'à **l'ouverture** d'une saisie.
   * En dépendance de l'effet, changer le sujet de l'en-tête pendant la frappe écraserait le
   * choix fait pour cette note-là.
   */
  const [sujetSaisi, setSujetSaisi] = useState('');
  const sujetParDefaut = useRef(sujet);
  sujetParDefaut.current = sujet;
  const [enregistrement, setEnregistrement] = useState(false);
  const champRef = useRef(null);
  const conteneurRef = useRef(null);

  /** Une ancre par mois, pour pouvoir défiler jusqu'à lui. Clé `YYYY-MM`. */
  const ancresMois = useRef(new Map());
  /** Mois vers lequel défiler dès que son ancre existe — l'allongement précède le défilement. */
  const cibleDefilement = useRef(null);
  /** Hauteur sous le point de vue avant un ajout en tête, pour ne pas déplacer l'image. */
  const compensation = useRef(null);
  /** Un allongement par rendu : l'événement `scroll` se répète bien avant que le DOM suive. */
  const allongementEnCours = useRef(false);
  /** Ordonnée du curseur, pour l'entraînement en bord de zone pendant un tracé. */
  const ordonneeSouris = useRef(0);

  const [noteEditee, setNoteEditee] = useState(null);
  const [noteSurvolee, setNoteSurvolee] = useState(null);

  // ---------------------------------------------------------------- La grille

  /**
   * Les semaines affichées, du lundi au dimanche.
   *
   * La grille déborde sur les mois voisins — c'est ce qui permet à une note à cheval sur deux
   * mois de se voir des deux côtés. Le nombre de semaines est calculé et non fixé à six : une
   * dernière ligne entièrement vide n'apprendrait rien.
   */
  const grille = useMemo(() => {
    const premier = plage.premier;
    const dernier = new Date(plage.premier.getFullYear(), plage.premier.getMonth() + plage.nb, 0);

    return { debut: premier, fin: dernier, premier, dernier };
  }, [plage]);

  /**
   * Le ruban à rendre : chaque mois, son titre puis ses semaines.
   *
   * **Un mois repart toujours à la ligne.** Une semaine à cheval mêlait deux mois sous un
   * seul titre : les 27 au 31 juillet se lisaient comme du mois d'août. Ici la ligne
   * s'interrompt à la fin du mois, les cases restantes sont laissées vides, et le mois
   * suivant recommence sur sa propre ligne — le titre est donc toujours en tête.
   *
   * **Le 1er occupe toujours la première case**, et les jours se suivent de sept en sept.
   * Les colonnes ne désignent donc plus un jour de semaine — c'est pourquoi l'en-tête
   * LUN…DIM a disparu et que chaque case porte son jour en toutes lettres : sans cela, rien
   * ne distinguerait plus un samedi d'un mardi.
   *
   * Chaque jour n'existe qu'une fois dans le DOM, sans quoi le tracé sélectionnerait deux
   * copies du même jour.
   */
  const ruban = useMemo(() => {
    const elements = [];

    for (let i = 0; i < plage.nb; i++) {
      const premier = new Date(plage.premier.getFullYear(), plage.premier.getMonth() + i, 1);
      const dernierJour = new Date(premier.getFullYear(), premier.getMonth() + 1, 0).getDate();
      elements.push({ type: 'mois', date: premier });

      let semaine = [];
      let rang = 0;

      const poser = () => {
        while (semaine.length < 7) semaine.push(null);
        elements.push({ type: 'semaine', jours: semaine, cle: `${cleMois(premier)}-${rang++}` });
        semaine = [];
      };

      for (let j = 1; j <= dernierJour; j++) {
        semaine.push(new Date(premier.getFullYear(), premier.getMonth(), j));
        if (semaine.length === 7) poser();
      }
      if (semaine.length > 0) poser();
    }

    return elements;
  }, [plage]);

  const feries = useMemo(() => feriesDeLaPeriode(grille.debut, grille.fin), [grille]);

  // ---------------------------------------------------------------- Les notes

  const chargerNotes = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      /**
       * Les bornes sont celles du **ruban entier**, pas du mois en tête : tous ses jours sont
       * atteignables au défilement, et une note posée sur l'un d'eux doit s'y voir.
       *
       * Ni `accountId` ni `platform` ne sont transmis : sans `siteId`, le serveur ajoute
       * silencieusement `{ siteId: null }` et tronque le résultat.
       */
      const params = new URLSearchParams({
        startDate: enIso(grille.debut),
        endDate: enIso(grille.fin)
      });
      if (clientId) params.append('clientId', clientId);
      if (siteId) params.append('siteId', siteId);
      // Le sujet filtre l'affichage au même titre que le client et le site. Il était le seul
      // sélecteur du bandeau à ne pas être transmis : le choisir ne changeait donc rien au
      // calendrier, alors que les deux autres le refiltraient aussitôt.
      if (sujet) params.append('topic', sujet);

      const reponse = await axios.get(`${API_URL}/api/notes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(reponse.data.notes || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de charger les notes de cette période');
    } finally {
      setChargement(false);
    }
  }, [token, grille, clientId, siteId, sujet]);

  useEffect(() => { chargerNotes(); }, [chargerNotes]);

  /** Notes couvrant chaque jour, indexées par date. Un seul parcours plutôt qu'un par case. */
  const notesParJour = useMemo(() => {
    const index = new Map();
    for (const note of notes) {
      const debut = (note.startDate || '').split('T')[0];
      const fin = (note.endDate || note.startDate || '').split('T')[0];
      if (!debut) continue;
      for (let j = new Date(`${debut}T00:00:00`); enIso(j) <= fin; j = decale(j, 1)) {
        const cle = enIso(j);
        if (!index.has(cle)) index.set(cle, []);
        index.get(cle).push(note);
      }
    }
    return index;
  }, [notes]);

  // ---------------------------------------------------------------- Le tracé

  /** Bornes de la sélection en cours, remises à l'endroit : on peut tracer à rebours. */
  const selection = useMemo(() => {
    if (saisie) return saisie;
    if (!ancre || !curseur) return null;
    const [debut, fin] = ancre <= curseur ? [ancre, curseur] : [curseur, ancre];
    return { debut, fin };
  }, [ancre, curseur, saisie]);

  const estSelectionne = (iso) => Boolean(selection && iso >= selection.debut && iso <= selection.fin);

  /**
   * Le relâchement est écouté sur `window`, pas sur la grille.
   *
   * Relâcher hors du calendrier — c'est le cas dès qu'on déborde en glissant — doit terminer
   * le tracé. Sur la grille seule, la sélection resterait collée au curseur.
   */
  useEffect(() => {
    const terminer = () => {
      if (!traceEnCours.current) return;
      traceEnCours.current = false;

      const a = ancreRef.current;
      const c = curseurRef.current;
      poserAncre(null);
      poserCurseur(null);

      if (!a || !c) return;
      // Le tracé peut aller à rebours : on remet les bornes à l'endroit.
      const [debut, fin] = a <= c ? [a, c] : [c, a];
      setSaisie({ debut, fin });
      setTexte('');
    };
    window.addEventListener('mouseup', terminer);
    return () => window.removeEventListener('mouseup', terminer);
  }, []);

  useEffect(() => { if (saisie && champRef.current) champRef.current.focus(); }, [saisie]);

  // `saisie` est un objet neuf à chaque sélection : l'effet ne rejoue donc pas pendant la
  // frappe, et le sujet choisi pour cette note tient jusqu'à son enregistrement.
  useEffect(() => { if (saisie) setSujetSaisi(sujetParDefaut.current || ''); }, [saisie]);

  const annulerSaisie = () => { setSaisie(null); setTexte(''); };

  /**
   * Deux issues qui ne dépendent pas du focus : Échap, et le clic hors de la grille.
   *
   * L'`onKeyDown` du champ ne suffit pas — si le focus n'a pas été pris, la touche ne lui
   * parvient jamais et la saisie devient une souricière. L'écouteur est donc sur la fenêtre.
   * Le clic, lui, est testé contre la grille : à l'intérieur, c'est la case elle-même qui
   * décide, et l'annuler ici tuerait la sélection qu'on vient d'ouvrir.
   */
  useEffect(() => {
    if (!saisie) return undefined;

    const auClavier = (e) => { if (e.key === 'Escape') annulerSaisie(); };
    const aLaSouris = (e) => { if (!conteneurRef.current?.contains(e.target)) annulerSaisie(); };

    window.addEventListener('keydown', auClavier);
    window.addEventListener('mousedown', aLaSouris);
    return () => {
      window.removeEventListener('keydown', auClavier);
      window.removeEventListener('mousedown', aLaSouris);
    };
  }, [saisie]);

  // ------------------------------------------------------- Le ruban défilant

  /** La plage, lisible depuis `allonger` sans le lier à chaque rendu. */
  const plageRef = useRef(plage);
  plageRef.current = plage;



  /**
   * Allonger le ruban d'un côté ou de l'autre.
   *
   * En tête, la hauteur ajoutée pousserait tout vers le bas : on relève la hauteur restant
   * sous le point de vue avant l'ajout, et un effet de mise en page rétablit `scrollTop`
   * après. Sans cela, chaque allongement ferait sauter le calendrier — et interromprait le
   * tracé que l'on est précisément en train de faire.
   */
  const allonger = useCallback((cote) => {
    const el = conteneurRef.current;
    // Le verrou n'est levé qu'une fois le DOM à jour : `scroll` se répète bien plus vite.
    if (allongementEnCours.current || !el || plageRef.current.nb >= PLAGE_MAX) return;

    allongementEnCours.current = true;
    if (cote === 'haut') {
      compensation.current = el.scrollHeight - el.scrollTop;
      setPlage(p => ({
        premier: new Date(p.premier.getFullYear(), p.premier.getMonth() - PAS, 1),
        nb: p.nb + PAS
      }));
    } else {
      setPlage(p => ({ premier: p.premier, nb: p.nb + PAS }));
    }
  }, []);

  useLayoutEffect(() => {
    const el = conteneurRef.current;
    allongementEnCours.current = false;
    if (!el) return;

    if (compensation.current != null) {
      el.scrollTop = el.scrollHeight - compensation.current;
      compensation.current = null;
    }

    if (cibleDefilement.current) {
      const ancre = ancresMois.current.get(cibleDefilement.current);
      if (ancre) {
        el.scrollTop = ancre.offsetTop;
        cibleDefilement.current = null;
      }
    }
  }, [plage]);

  /**
   * Le mois écrit dans l'en-tête : celui dont la bande est la plus basse au-dessus du bord.
   *
   * On cherche le maximum, pas le dernier rencontré : les rappels de `ref` étant recréés à
   * chaque rendu, React efface puis réinscrit toutes les ancres, et l'ordre d'insertion de la
   * `Map` ne suit plus le calendrier dès qu'on allonge par le haut.
   */
  const relireMoisVisible = useCallback(() => {
    const el = conteneurRef.current;
    if (!el) return;
    let courant = null;
    let plusBasse = -Infinity;
    for (const [cle, ancre] of ancresMois.current) {
      if (ancre && ancre.offsetTop <= el.scrollTop + 8 && ancre.offsetTop > plusBasse) {
        plusBasse = ancre.offsetTop;
        courant = cle;
      }
    }
    if (!courant) return;
    const [annee, mois] = courant.split('-').map(Number);
    setMoisVisible(m => (m.getFullYear() === annee && m.getMonth() === mois - 1
      ? m
      : new Date(annee, mois - 1, 1)));
  }, []);

  const auDefilement = useCallback(() => {
    const el = conteneurRef.current;
    if (!el) return;
    relireMoisVisible();
    if (el.scrollTop < SEUIL_BORD) allonger('haut');
    else if (el.scrollHeight - el.scrollTop - el.clientHeight < SEUIL_BORD) allonger('bas');
  }, [allonger, relireMoisVisible]);

  /** Aller à un mois, en allongeant d'abord le ruban s'il ne le contient pas encore. */
  const changerDeMois = (n) => {
    const cible = new Date(moisVisible.getFullYear(), moisVisible.getMonth() + n, 1);
    const cle = cleMois(cible);
    const ancre = ancresMois.current.get(cle);
    const el = conteneurRef.current;

    if (ancre && el) {
      el.scrollTop = ancre.offsetTop;
      setMoisVisible(cible);
      return;
    }

    cibleDefilement.current = cle;
    allonger(n < 0 ? 'haut' : 'bas');
    setMoisVisible(cible);
  };

  /** Au montage, le ruban s'ouvre sur le mois courant et non sur son premier mois. */
  useLayoutEffect(() => {
    const a = new Date();
    cibleDefilement.current = cleMois(a);
    const el = conteneurRef.current;
    const ancre = ancresMois.current.get(cleMois(a));
    if (el && ancre) {
      el.scrollTop = ancre.offsetTop;
      cibleDefilement.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Entraînement en bord de zone pendant un tracé.
   *
   * Bouton enfoncé, on ne peut pas atteindre un mois hors de la zone visible : approcher le
   * bord fait donc défiler. La molette reste utilisable, mais s'en remettre à elle seule
   * rendrait la sélection sur plusieurs mois pénible.
   */
  useEffect(() => {
    const suivre = (e) => { ordonneeSouris.current = e.clientY; };
    const battre = () => {
      const el = conteneurRef.current;
      if (!el || !traceEnCours.current) return;
      const zone = el.getBoundingClientRect();
      const y = ordonneeSouris.current;
      if (y < zone.top + BORD_ENTRAINEMENT) el.scrollTop -= VITESSE;
      else if (y > zone.bottom - BORD_ENTRAINEMENT) el.scrollTop += VITESSE;
    };
    window.addEventListener('mousemove', suivre);
    const battement = setInterval(battre, 30);
    return () => {
      window.removeEventListener('mousemove', suivre);
      clearInterval(battement);
    };
  }, []);

  /**
   * Ce sur quoi porte une note : client › site › compte.
   *
   * Sans elle, deux notes du même jour sur deux clients différents sont indiscernables dans
   * la grille — et l'infobulle, qui est le seul endroit où la note se lit en entier, ne disait
   * rien de ce à quoi elle se rattache.
   *
   * Le nom du compte se retrouve dans les comptes connus ; à défaut on montre son identifiant
   * plutôt que rien, une note pouvant viser un compte détaché depuis.
   */
  const porteeDeNote = useCallback((note) => {
    const parties = [];
    if (note.clientName) parties.push(note.clientName);
    if (note.siteName) parties.push(note.siteName);
    if (note.accountId) {
      const compte = accounts.find(a => a.sourceId === note.accountId);
      parties.push(compte?.name || note.accountId);
    } else if (note.platform) {
      parties.push(libellePlateforme(note.platform));
    }
    return parties;
  }, [accounts]);

  const enregistrer = async () => {
    if (!texte.trim() || !saisie) return;
    setEnregistrement(true);
    setErreur('');
    try {
      const reponse = await axios.post(`${API_URL}/api/notes`, {
        content: texte.trim(),
        // La portée vient du hook partagé, qui en compose déjà la forme attendue par l'API —
        // plateforme comprise, déduite du compte choisi.
        ...portee.corpsDePortee,
        // Le sujet de la note, et non celui de l'en-tête : ce dernier n'en est que la valeur
        // d'office, et il sert aussi de filtre d'affichage.
        topic: sujetSaisi || null,
        startDate: saisie.debut,
        endDate: saisie.fin,
        showOnChart: surGraphique
      }, { headers: { Authorization: `Bearer ${token}` } });

      // La note rejoint la liste locale : rien dans l'application ne rafraîchit les notes
      // après une création, et attendre un rechargement casserait l'enchaînement.
      if (reponse.data?.note) setNotes(actuelles => [...actuelles, reponse.data.note]);
      annulerSaisie();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible d\'enregistrer la note');
    } finally {
      setEnregistrement(false);
    }
  };

  // ---------------------------------------------------------------- Rendu

  const aujourdhui = enIso(new Date());

  return (
    <div className="step-content">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>
          <IconeCalendrier size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Calendrier des notes
        </h2>
        {chargement && <Loader size={14} className="spin" style={{ color: '#94a3b8' }} />}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={() => changerDeMois(-1)}
            title="Mois précédent"
            style={{ ...STYLE_SELECTEUR, padding: '8px', display: 'flex' }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ minWidth: '150px', textAlign: 'center', fontWeight: 600, fontSize: '15px' }}>
            {MOIS[moisVisible.getMonth()]} {moisVisible.getFullYear()}
          </span>
          <button
            onClick={() => changerDeMois(1)}
            title="Mois suivant"
            style={{ ...STYLE_SELECTEUR, padding: '8px', display: 'flex' }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Portée et sujet : ils valent pour toutes les notes saisies ensuite, jusqu'à ce qu'on
          en change. C'est ce qui permet d'enchaîner sans reprendre les mêmes réglages. */}
      <PorteeDeNote portee={portee} clients={clients} />

      {erreur && (
        <div style={{
          padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5', color: '#991b1b', fontSize: '13px', marginBottom: '12px'
        }}>
          {erreur}
        </div>
      )}

      {/* Le ruban. `position: relative` est nécessaire : les ancres de mois se repèrent par
          `offsetTop`, qui se mesure depuis le premier parent positionné. */}
      <div
        ref={conteneurRef}
        onScroll={auDefilement}
        style={{
          position: 'relative',
          maxHeight: '62vh',
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          paddingRight: '4px'
        }}
      >
      {ruban.map(element => element.type === 'mois' ? (
        <React.Fragment key={`m-${cleMois(element.date)}`}>
          {/* Ancre de mesure, sans `sticky`.
              `offsetTop` d'un élément collant rend sa position **collée**, pas sa position
              dans le flux : lue sur la bande elle-même, elle valait le défilement courant, et
              « aller au mois précédent » ne bougeait de rien. Ce repère de hauteur nulle,
              lui, ne se déplace jamais. */}
          <div
            ref={noeud => {
              if (noeud) ancresMois.current.set(cleMois(element.date), noeud);
              else ancresMois.current.delete(cleMois(element.date));
            }}
            style={{ height: 0 }}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: `${ECART}px`,
              position: 'sticky',
              top: 0,
              zIndex: 4,
              marginBottom: `${ECART}px`,
              // Fond opaque : collé en haut, le titre doit masquer les semaines qui défilent
              // dessous.
              backgroundColor: '#FAF9F5'
            }}
          >
            <div style={{
              gridColumn: '1 / -1',
              padding: '8px 4px 6px',
              fontSize: '13px',
              fontWeight: 700,
              color: '#7c2d12',
              textTransform: 'capitalize'
            }}>
              {MOIS[element.date.getMonth()]} {element.date.getFullYear()}
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div
          key={element.cle}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: `${ECART}px`, marginBottom: `${ECART}px` }}
        >
          {element.jours.map((jour, colonne) => {
            // Une case vide comble le début ou la fin d'un mois. Elle garde sa place dans la
            // grille pour que les colonnes restent alignées sur les jours de semaine, mais
            // elle ne se dessine pas et ne réagit à rien : il n'y a pas de jour à y noter.
            if (!jour) return <div key={`vide-${colonne}`} />;

            const semaine = element.jours;
            const iso = enIso(jour);
            const weekend = jour.getDay() === 0 || jour.getDay() === 6;
            const ferie = feries.get(iso);
            const selectionne = estSelectionne(iso);
            const notesDuJour = notesParJour.get(iso) || [];
            // Repère de tête de ligne : une note qui court sur plusieurs jours réécrit son
            // libellé ici, sinon la suite de la note serait une barre muette.
            const premierDeLaLigne = enIso(semaine.find(Boolean));
            const saisieIci = saisie && saisie.debut === iso;
            // Le champ s'étire sur les jours sélectionnés de cette ligne, sans déborder.
            const joursDeLaLigne = saisieIci
              ? semaine.filter(j => j && enIso(j) >= saisie.debut && enIso(j) <= saisie.fin).length
              : 1;

            return (
              <div
                key={iso}
                onMouseDown={() => {
                  // Une saisie en cours ne bloque plus la grille : recliquer ailleurs
                  // l'abandonne et repart d'une sélection neuve, comme dans un agenda.
                  if (saisie) annulerSaisie();
                  traceEnCours.current = true;
                  poserAncre(iso);
                  poserCurseur(iso);
                }}
                onMouseEnter={() => { if (traceEnCours.current) poserCurseur(iso); }}
                style={{
                  position: 'relative',
                  minHeight: '92px',
                  padding: '6px',
                  borderRadius: '8px',
                  border: iso === aujourdhui ? '2px solid #7c2d12' : '1px solid #e5e7eb',
                  backgroundColor: selectionne ? '#fff7ed'
                    : ferie ? '#fdf2f8'
                      : weekend ? 'rgba(251, 146, 60, 0.12)'
                        : 'white',
                  cursor: saisie ? 'default' : 'cell',
                  userSelect: 'none',
                  overflow: saisieIci ? 'visible' : 'hidden'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                    {jour.getDate()}
                  </span>
                  {/* Le jour de semaine se lit dans la case, faute de pouvoir se lire en
                      tête de colonne : le 1er ouvrant toujours la ligne, une colonne ne
                      désigne plus un jour fixe. */}
                  <span style={{ fontSize: '10px', color: weekend ? '#9a6b4f' : '#9ca3af', textTransform: 'uppercase' }}>
                    {JOURS[(jour.getDay() + 6) % 7]}
                  </span>
                  {ferie && (
                    <span style={{ fontSize: '9px', color: '#be185d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ferie}
                    </span>
                  )}
                </div>

                {/* Les notes déjà posées. Le libellé n'est écrit qu'au premier jour de la note
                    ou au début de la ligne : répété dans chaque case, il ferait illisible une
                    note d'une semaine. */}
                {notesDuJour.map(note => {
                  const debut = (note.startDate || '').split('T')[0];
                  const montrerTexte = iso === debut || iso === premierDeLaLigne;
                  return (
                    <div
                      key={note.id}
                      onMouseDown={e => e.stopPropagation()}
                      onClick={() => setNoteEditee(note)}
                      onMouseEnter={e => {
                        const zone = e.currentTarget.getBoundingClientRect();
                        setNoteSurvolee({ note, x: zone.left, bas: zone.bottom });
                      }}
                      onMouseLeave={() => setNoteSurvolee(null)}
                      title="Modifier cette note"
                      style={{
                        marginBottom: '2px',
                        padding: '2px 5px',
                        borderRadius: '3px',
                        fontSize: '10px',
                        lineHeight: 1.3,
                        cursor: 'pointer',
                        color: 'white',
                        backgroundColor: couleurDeNote(note),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minHeight: '16px'
                      }}
                    >
                      {montrerTexte ? note.content : ' '}
                    </div>
                  );
                })}

                {saisieIci && (
                  <div
                    onMouseDown={e => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: '6px',
                      right: joursDeLaLigne > 1 ? 'auto' : '6px',
                      bottom: '6px',
                      width: joursDeLaLigne > 1
                        ? `calc(${joursDeLaLigne} * 100% + ${(joursDeLaLigne - 1) * ECART}px - 12px)`
                        : 'auto',
                      zIndex: 5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px'
                    }}
                  >
                    {/* Le sujet de cette note-là, au-dessus du texte comme dans la modale.
                        Une liste et non des pastilles : elle tient dans la largeur d'une case
                        là où cinq libellés déborderaient sur les jours voisins, et son menu
                        natif n'est jamais rogné par la grille. */}
                    <select
                      value={sujetSaisi}
                      onChange={e => setSujetSaisi(e.target.value)}
                      disabled={enregistrement}
                      title="Sujet de cette note"
                      style={{
                        width: '100%', maxWidth: '100%', minWidth: 0,
                        // Même taille de police que le champ de note, juste dessous : deux
                        // corps différents dans un bloc de deux lignes se lisent comme une
                        // hiérarchie, alors que les deux champs sont de même rang.
                        padding: '4px 6px', fontSize: '12px',
                        // Le cadre d'attention est ici, et non sur le champ de note : le sujet
                        // est le premier des deux à renseigner, et c'est celui qu'on oublie.
                        // Une fois choisi, le cadre prend sa couleur et cesse d'appeler.
                        border: `1px solid ${sujetDeNote(sujetSaisi)?.couleur || '#7c2d12'}`,
                        borderRadius: '5px', outline: 'none', cursor: 'pointer',
                        backgroundColor: sujetDeNote(sujetSaisi)?.fond || 'white',
                        color: sujetDeNote(sujetSaisi)?.couleur || '#6b7280',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {/* Une consigne, pas une valeur : la ligne vide enregistre bien une
                          note sans sujet — l'état de toutes celles d'avant la table des
                          sujets — mais « Sans sujet » se lisait comme un choix déjà fait. */}
                      <option value="">Choisir un sujet</option>
                      {NOTE_TOPICS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>

                    <div style={{ display: 'flex', gap: '4px', minWidth: 0 }}>
                    <input
                      ref={champRef}
                      value={texte}
                      onChange={e => setTexte(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); enregistrer(); }
                        if (e.key === 'Escape') { e.preventDefault(); annulerSaisie(); }
                      }}
                      placeholder={saisie.debut === saisie.fin
                        ? 'Note… (Entrée pour enregistrer)'
                        : `Note du ${enFrancais(saisie.debut)} au ${enFrancais(saisie.fin)}…`}
                      disabled={enregistrement}
                      style={{
                        flex: 1, minWidth: 0, padding: '5px 8px', fontSize: '12px',
                        border: '1px solid #d1d5db', borderRadius: '5px', outline: 'none'
                      }}
                    />
                    {/* Une sortie visible. La touche Échap ne se devine pas, et une saisie
                        dont on ne sait pas comment sortir est une souricière. */}
                    <button
                      onClick={annulerSaisie}
                      disabled={enregistrement}
                      title="Annuler (Échap)"
                      style={{
                        flexShrink: 0, width: '24px', height: '26px', padding: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid #e5e7eb', borderRadius: '5px',
                        backgroundColor: 'white', color: '#6b7280', cursor: 'pointer'
                      }}
                    >
                      <X size={13} />
                    </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      </div>

      {/* Infobulle des notes. En portail et en position fixe, comme celles du tableau
          consolidé : dans le flux, elle serait rognée par la case. */}
      {noteSurvolee && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          left: Math.min(noteSurvolee.x, window.innerWidth - 340),
          top: noteSurvolee.bas + 6,
          width: '320px', zIndex: 10000,
          backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 12px',
          fontSize: '0.8125rem', color: '#1f2937', pointerEvents: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            {sujetDeNote(noteSurvolee.note.topic) && (
              <span style={{
                padding: '1px 7px', borderRadius: '20px', fontSize: '0.625rem', fontWeight: 500,
                color: sujetDeNote(noteSurvolee.note.topic).couleur,
                backgroundColor: sujetDeNote(noteSurvolee.note.topic).fond
              }}>
                {sujetDeNote(noteSurvolee.note.topic).label}
              </span>
            )}
            <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>
              {enFrancais((noteSurvolee.note.startDate || '').split('T')[0])}
              {noteSurvolee.note.endDate && noteSurvolee.note.endDate !== noteSurvolee.note.startDate
                && ` → ${enFrancais(noteSurvolee.note.endDate.split('T')[0])}`}
            </span>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{noteSurvolee.note.content}</div>

          {/* La portée en pied, séparée du contenu : c'est une étiquette, pas la note. Une
              note sans portée est valable pour tout, et le dire vaut mieux que se taire. */}
          <div style={{
            marginTop: '8px', paddingTop: '6px', borderTop: '1px solid #f1f5f9',
            fontSize: '0.6875rem', color: '#64748b',
            display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap'
          }}>
            <Target size={11} style={{ flexShrink: 0, color: '#94a3b8' }} />
            {porteeDeNote(noteSurvolee.note).length > 0
              ? porteeDeNote(noteSurvolee.note).map((partie, i) => (
                <React.Fragment key={partie}>
                  {i > 0 && <span style={{ color: '#cbd5e1' }}>›</span>}
                  <span>{partie}</span>
                </React.Fragment>
              ))
              : <span style={{ fontStyle: 'italic' }}>Tous les clients et comptes</span>}
          </div>
        </div>,
        document.body
      )}

      <NotesModal
        isOpen={Boolean(noteEditee)}
        onClose={() => setNoteEditee(null)}
        onSave={(note) => {
          // Remplacement en place : la grille doit refléter la correction sans rechargement.
          setNotes(actuelles => actuelles.map(n => (n.id === note.id ? { ...n, ...note } : n)));
          setNoteEditee(null);
        }}
        editingNote={noteEditee}
        clients={clients}
        sites={sites}
        accounts={accounts}
        token={token}
      />
    </div>
  );
};

export default NotesCalendar;
